import React, { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, useParams, useSearchParams } from "react-router-dom";
import { setPreload } from "../../../server/CartsOpen";
import AddProductToCategoryDialog from "../products/AddProductToCategoryDialog";
import { useDispatch, useSelector } from "react-redux";
import { getBySlug } from "../../../server/Category";
import { Search, X, Pencil, Plus } from "lucide-react";
import {
  formatProductPackSize,
  getProductPackSizeSortValue,
} from "../../../utils/formatProductPackSize";
import { withProductStatus } from "../../../utils/productStatus";
import { useProductStatusToggle } from "../../../hooks/useProductStatusToggle";
import ProductStatusBadge from "../../productStatus/ProductStatusBadge";
import {
  extractProductsFromTree,
  getCategoryLevel,
} from "../products/useCategoryUtils";

const normalizeCategoryTreeNode = (node) => {
  if (!node) return { products: [], children: [] };
  return {
    ...node,
    products: Array.isArray(node.products) ? node.products : [],
    children: Array.isArray(node.children)
      ? node.children.map(normalizeCategoryTreeNode)
      : [],
  };
};

const getCategoryProductKey = (product) =>
  product.productId ??
  product.id ??
  [product.gtin, product.variant]
    .filter((v) => v != null && v !== "")
    .join("-");

const collectCategoryProducts = (node) => {
  const products = extractProductsFromTree(normalizeCategoryTreeNode(node));
  const seen = new Set();
  return products.filter((product) => {
    const key = getCategoryProductKey(product);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const ProductsEdit = () => {
  const dispatch = useDispatch();
  const [_searchParams] = useSearchParams();
  const { slug } = useParams();

  const stationId = useSelector((state) => state.user.stationId);
  const categoriesTree = useSelector((state) => state.category.categories);
  const [category, setCategory] = useState(null);
  const [product, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });

  const refreshProducts = useCallback(() => {
    if (!slug || !stationId) return;
    dispatch(setPreload(true));
    getBySlug(stationId, slug, false, true)
      .then((res) => {
        if (!res) {
          setProducts([]);
          return;
        }
        setProducts(collectCategoryProducts(res));
        setCategory({ id: res.id, name: res.name, isFamily: res.isFamily });
      })
      .catch((err) => console.error(err))
      .finally(() => dispatch(setPreload(false)));
  }, [slug, stationId, dispatch]);

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  const applyLocalProductStatus = useCallback((productId, status) => {
    const id = String(productId);
    setProducts((prev) =>
      prev.map((p) =>
        String(p.productId ?? p.id ?? "") === id
          ? withProductStatus(p, status)
          : p,
      ),
    );
  }, []);

  const { setProductStatus, statusPendingByProductId } = useProductStatusToggle(
    applyLocalProductStatus,
  );

  const getSortValue = useCallback((item, key) => {
    switch (key) {
      case "name":
        return item.name || "";
      case "brandName":
        return item.brand?.name || "";
      case "gtin":
        return item.gtin || "";
      case "packSize":
        return getProductPackSizeSortValue(item);
      case "status":
        return item.status || "";
      default:
        return "";
    }
  }, []);

  const displayedProducts = useMemo(() => {
    const filtered = product.filter((item) =>
      search ? item.name?.includes(search) : true,
    );
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);
      const comparison = String(aValue).localeCompare(String(bValue), "he", {
        numeric: true,
        sensitivity: "base",
      });
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
    return sorted;
  }, [product, search, sortConfig, getSortValue]);

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  return (
    <div className="products-edit">
      <h1 className="title-role">
        ניהול מוצרים
        {category?.name ? ` — ${category.name}` : ""}
      </h1>

      <div className="items-container">
        <div className="products-edit-toolbar">
          <button
            type="button"
            className="add-product"
            onClick={() => setIsDialogOpen(true)}
          >
            <Plus aria-hidden size={18} strokeWidth={2} />
            <span>הוסף מוצר</span>
          </button>

          <div className="search">
            <input
              onChange={({ target }) => setSearch(target.value)}
              value={search}
              type="text"
              placeholder="חיפוש מוצר..."
            />
            <button
              type="button"
              className="search-icon-btn"
              onClick={() => setSearch("")}
              aria-label={search ? "נקה חיפוש" : "חיפוש"}
              title={search ? "נקה חיפוש" : "חיפוש"}
            >
              {search ? (
                <X aria-hidden size={18} strokeWidth={2} />
              ) : (
                <Search aria-hidden size={18} strokeWidth={2} />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="product-edit-table">
        <table>
          <thead>
            <tr>
              <th>פרטים</th>
              <th>
                <button
                  type="button"
                  className={`sort-header-btn${
                    sortConfig.key === "name" ? " sort-header-btn--active" : ""
                  }`}
                  onClick={() => toggleSort("name")}
                >
                  <span>שם המוצר</span>
                  <span className="sort-indicator">
                    {renderSortIndicator("name")}
                  </span>
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className={`sort-header-btn${
                    sortConfig.key === "packSize"
                      ? " sort-header-btn--active"
                      : ""
                  }`}
                  onClick={() => toggleSort("packSize")}
                >
                  <span>מארז / נפח</span>
                  <span className="sort-indicator">
                    {renderSortIndicator("packSize")}
                  </span>
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className={`sort-header-btn${
                    sortConfig.key === "brandName"
                      ? " sort-header-btn--active"
                      : ""
                  }`}
                  onClick={() => toggleSort("brandName")}
                >
                  <span>מותג</span>
                  <span className="sort-indicator">
                    {renderSortIndicator("brandName")}
                  </span>
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className={`sort-header-btn${
                    sortConfig.key === "gtin" ? " sort-header-btn--active" : ""
                  }`}
                  onClick={() => toggleSort("gtin")}
                >
                  <span>ברקוד</span>
                  <span className="sort-indicator">
                    {renderSortIndicator("gtin")}
                  </span>
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className={`sort-header-btn${
                    sortConfig.key === "status"
                      ? " sort-header-btn--active"
                      : ""
                  }`}
                  onClick={() => toggleSort("status")}
                >
                  <span>סטטוס</span>
                  <span className="sort-indicator">
                    {renderSortIndicator("status")}
                  </span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayedProducts.map((el) => (
              <tr key={getCategoryProductKey(el)}>
                <td className="enter-img">
                  <NavLink
                    to={`/edit-product/${el.productId}/information`}
                    state={{ from: `/products-edit/${slug}` }}
                    className="details-link"
                  >
                    <Pencil aria-hidden size={16} strokeWidth={2} />
                    <span>לפרטים</span>
                  </NavLink>
                </td>
                <td>{el.name}</td>
                <td className="pack-size-cell">{formatProductPackSize(el)}</td>
                <td>{el.brand?.name || "-"}</td>
                <td>{el.gtin}</td>
                <td className="status-cell">
                  <ProductStatusBadge
                    status={el.status}
                    pending={
                      !!statusPendingByProductId[
                        String(el.productId ?? el.id ?? "")
                      ]
                    }
                    onStatusChange={(nextStatus) =>
                      setProductStatus(el, slug, nextStatus)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddProductToCategoryDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        categorySlug={slug}
        categoryId={category?.id}
        categoryName={category?.name ?? ""}
        categoryLevel={getCategoryLevel(slug, categoriesTree) || undefined}
        isFamily={Boolean(category?.isFamily)}
        onSuccess={refreshProducts}
      />
    </div>
  );
};

export default ProductsEdit;
